/**
 * DemoModeBanner — floating pill shown while a read-only demo session is active.
 * Explains why writes are blocked and drives sign-up conversion.
 *
 * Mounted app-wide in App.tsx (alongside EmulationBanner) so it persists across
 * routes. Renders nothing outside a demo session. Follows the EmulationBanner
 * fixed-pill pattern — including its placement: it floats just BELOW the app
 * header rather than over it, because the header is HEADER_HEIGHT tall and the
 * global search pill is vertically centered inside it, so a pill anchored at the
 * top of the viewport sits exactly on top of the search bar no matter how short
 * it is. See docs/platform/demo-without-login-design.md.
 */
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/button';
import { useAuth } from '../../auth';
import { isDemoMode } from '../../auth/demoSession';
import { HEADER_CLEARANCE_TOP } from '../../styles/layout.constants';

export function DemoModeBanner() {
  const { t } = useTranslation('auth');
  const { logout } = useAuth();

  if (!isDemoMode()) return null;

  const handleExit = async () => {
    await logout();
    window.location.assign('/login');
  };

  // Leave the demo before heading to sign-up — otherwise PublicRoute treats the
  // still-present demo token as an authenticated session and bounces /register
  // back to the app home.
  const handleSignUp = async () => {
    await logout();
    window.location.assign('/register');
  };

  return (
    <div
      data-testid="demo-mode-banner"
      style={{ top: HEADER_CLEARANCE_TOP }}
      className="fixed left-1/2 z-[1300] -translate-x-1/2 flex max-w-[95vw] items-center gap-2 rounded-md bg-primary px-2.5 py-0.5 text-primary-foreground shadow-md"
    >
      <Eye className="h-3.5 w-3.5 shrink-0" />
      {/* At 375px the pill spans the viewport and the two ACTIONS are what must
          survive, so the explanatory message is the token that gives up width. */}
      <span className="text-[11px] sm:text-xs font-medium truncate min-w-0">{t('demo.banner')}</span>
      <Button
        size="sm"
        variant="secondary"
        className="h-5 shrink-0 px-1.5 text-[11px]"
        onClick={handleSignUp}
        data-testid="demo-banner-signup"
      >
        <span className="hidden sm:inline">{t('demo.bannerCta')}</span>
        <span className="sm:hidden">{t('demo.bannerCtaShort')}</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleExit}
        className="h-5 shrink-0 px-1.5 text-[11px] text-inherit hover:bg-primary-foreground/10"
        data-testid="demo-banner-exit"
      >
        {t('demo.exit')}
      </Button>
    </div>
  );
}
